import { useEffect } from 'preact/hooks';

import { useDangerousLatestValueRef } from '@/hooks/useLatestValueRef';

import { BannerButton, ModalConfig } from '../types';
import FeedbackModal from './ui/FeedbackModal';
import TourVideoModal from './ui/TourVideoModal';
import TzPlusWelcomeModal from './ui/TzPlusWelcomeModal';
import WelcomeModal from './ui/WelcomeModal';

const MASCOT_SRC = 'https://zoolife.tv/assets/guidedTour/mascot/welcome.webp';
const MAP_SRC = 'https://zoolife.tv/assets/guidedTour/guided-tour-map-bg-transparent.png';

interface TourModalSlotProps {
  modal: ModalConfig;
  advanceTo: (stepId: string) => void;
  onButtonClick: (button: BannerButton) => void;
}

/**
 * Renders the step's full-screen modal. These use grommet <Layer>, which portals to the body,
 * so mounting inside the runner still overlays the whole page. Both transitions come from
 * config (`nextStepId` / `onCloseStepId`) rather than hardcoded step ids.
 */
const TourModalSlot = ({ modal, advanceTo, onButtonClick }: TourModalSlotProps) => {
  const advanceToRef = useDangerousLatestValueRef(advanceTo);
  const onClose = () => advanceTo(modal.onCloseStepId ?? modal.nextStepId ?? '');

  /**
   * A modal whose config carries a `button` goes through the controller's full button path, so it
   * navigates and reports like any banner button. Without one it just advances, as before.
   */
  const onPrimaryAction = () => {
    if (modal.button) {
      onButtonClick(modal.button);
      return;
    }
    if (modal.nextStepId) advanceTo(modal.nextStepId);
  };

  // A video step with no source would trap the user (banner stays hidden while modal is
  // "resolved"); fire the close transition so the tour can advance.
  useEffect(() => {
    if (modal.kind !== 'video' || modal.src) return;
    const fallback = modal.onCloseStepId ?? modal.nextStepId;
    if (fallback) advanceToRef.current(fallback);
  }, [modal.kind, modal.src, modal.onCloseStepId, modal.nextStepId, advanceToRef]);

  if (modal.kind === 'welcome') {
    return <WelcomeModal onStart={onPrimaryAction} mascotSrc={MASCOT_SRC} mapSrc={MAP_SRC} />;
  }

  if (modal.kind === 'tzWelcome') {
    return <TzPlusWelcomeModal onStart={onPrimaryAction} mascotSrc={MASCOT_SRC} />;
  }

  if (modal.kind === 'video') {
    if (!modal.src) return null;
    return <TourVideoModal src={modal.src} onClose={onClose} />;
  }

  return (
    <FeedbackModal
      isSubmitted={Boolean(modal.submitted)}
      onRatingSubmit={() => modal.nextStepId && advanceTo(modal.nextStepId)}
      onFeedbackComplete={onClose}
    />
  );
};

export default TourModalSlot;
